import {
    ButtonsOptions,
    ColorOptions,
    FormSizeOptions,
    HorizontalAlignmentOptions,
    HtmlTagOptions,
    LayoutWidthOptions,
    RatioOptions,
    TextStyleOptions,
    UndefinedOption
} from '@df/ui/atomic/atomic-options';
import { NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { hotspotsFields } from '../hotspots/cms-hotspots.config';
import { NCmsImageWithHotSpots } from './cms-image-with-hot-spots.interface';

export const CMS_COMPONENT_IMAGE_WITH_HOT_SPOTS_CONFIG = <Omit<NICmsContentComponent.IMeta<NICmsContentComponent.IComponent>, 'type'>>{
    id: 'image-with-hot-spots',
    name: 'Image with Hot Spots',
    image: '/common/cms/image-component.png',
    tags: ['image', 'big', 'banner'],
    fields: [
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-general',
            title: 'General'
        },
        <NICmsContentField.IChoiceField>{
            key: 'ratio',
            type: NICmsContentField.EFieldType.choice,
            title: 'Ratio',
            options: RatioOptions,
            description: 'Proportions width x height',
            default: 'ratio-2-1'
        },
        {
            key: 'image',
            type: NICmsContentField.EFieldType.image,
            title: 'Image',
            default: '',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'cta_link',
            type: NICmsContentField.EFieldType.text,
            title: 'Link'
        },
        // content
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-content',
            title: 'Content'
        },
        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            title: 'Content Width',
            key: 'width',
            itemName: 'width',
            options: LayoutWidthOptions,
            default: undefined
        },
        // Text blocks
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'blocks',
            title: 'Content Blocks',
            itemName: 'Block',
            itemNameField: 'block',
            defaultSize: 1,
            fields: [
                // general
                {
                    type: NICmsContentField.EFieldType.label,
                    key: 'blocks-general',
                    title: 'General'
                },
                {
                    key: 'custom',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Use Custom Components',
                    default: false
                },
                <NICmsContentField.IComponentsField>{
                    key: 'components',
                    type: NICmsContentField.EFieldType.components,
                    title: 'Components',
                    itemName: 'component',
                    default: [],
                    canShow: (data: NCmsImageWithHotSpots.IBlockData) => data.custom
                },
                {
                    key: 'style',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Custom Styling',
                    description: 'Direct CSS. Please be careful when changing this.',
                    canShow: (data: NCmsImageWithHotSpots.IBlockData) => !data.custom
                },
                // text posistioning
                {
                    type: NICmsContentField.EFieldType.label,
                    key: 'blocks-positioning',
                    title: 'Positioning'
                },
                <NICmsContentField.INumberField>{
                    key: 'left_position',
                    type: NICmsContentField.EFieldType.number,
                    title: 'Shift From Left',
                    min: 0,
                    max: 100,
                    step: 1,
                    default: 0,
                    canShow: (data: NCmsImageWithHotSpots.IBlockData) => !data.custom
                },
                <NICmsContentField.INumberField>{
                    key: 'top_position',
                    type: NICmsContentField.EFieldType.number,
                    title: 'Shift From Top',
                    min: 0,
                    max: 100,
                    step: 1,
                    default: 50
                },
                <NICmsContentField.INumberField>{
                    key: 'right_position',
                    type: NICmsContentField.EFieldType.number,
                    title: 'Shift From Right',
                    min: 0,
                    max: 100,
                    step: 1,
                    default: 0
                },
                // text lines
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'lines',
                    title: 'Text Lines',
                    itemName: 'Line',
                    itemNameField: 'text',
                    canShow: (data: NCmsImageWithHotSpots.IBlockData) => !data.custom,
                    defaultSize: 1,
                    fields: [
                        <NICmsContentField.IChoiceField>{
                            key: 'type',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Line Type',
                            options: [
                                { key: 'text', title: 'Text Line' },
                                { key: 'button', title: 'Button' }
                            ],
                            description: 'Line type',
                            default: 'text'
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'align',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Text Alignment',
                            options: HorizontalAlignmentOptions,
                            default: 'left'
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'button_class',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Button Style',
                            canShow: (data: NCmsImageWithHotSpots.ILineData) => data.type === 'button',
                            options: ButtonsOptions,
                            default: ButtonsOptions[0] ? ButtonsOptions[0].key : undefined
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'form_size',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Button Size',
                            canShow: (data: NCmsImageWithHotSpots.ILineData) => data.type === 'button',
                            options: FormSizeOptions
                        },
                        {
                            key: 'button_link',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Button Link',
                            description: 'If component link is set it will be used unless overriden here.',
                            canShow: (data: NCmsImageWithHotSpots.ILineData) => data.type === 'button'
                        },
                        {
                            key: 'text',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Copy',
                            default: 'This is new line'
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'text_style',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Text Style',
                            options: TextStyleOptions,
                            canShow: (data: NCmsImageWithHotSpots.ILineData) => data.type !== 'button'
                        },
                        {
                            key: 'style',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Custom Styling',
                            description: 'Direct CSS. Please be careful when changing this.',
                            protected: true
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'color',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Text Color',
                            options: ColorOptions,
                            colorPreview: true,
                            canShow: (data: NCmsImageWithHotSpots.ILineData) =>
                                data.type !== 'button' || data.button_class === 'button-inline'
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'tag',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Force Tag',
                            description: 'For SEO reasons you may want to force particular HTML tag',
                            options: [...UndefinedOption, ...HtmlTagOptions],
                            canShow: (data: NCmsImageWithHotSpots.ILineData) => data.type !== 'button'
                        }
                    ]
                }
            ]
        },
        ...hotspotsFields<NCmsImageWithHotSpots.IComponentData>(true)
    ]
};
